import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, Modal, Segmented, Table, Typography, Space, Select } from 'antd';
import ExpandCollapseAllButton from '../../common/ExpandCollapseAllButton';
import NumberInput from '../../common/NumberInput';
import { InfoCircleOutlined, FolderAddOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTender } from '../../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useCanConfigureBoq } from '@/hooks/useCanConfigureBoq';
import { useItemLibrary } from '../../../hooks/useItemLibrary';
import { useRoadsAndSewersMarkupFactor } from '../../../hooks/useRoadsAndSewersMarkupFactor';
import { useBoqLatestVersion } from '../../../hooks/useBillOfQuantities';
import { flattenBoqSummaryLineItems, type RoadsAndSewersBoqSummary } from '../../../utils/roadsAndSewersBoqSummaryComputer';
import { BcCalculationDetails, computeBcCalculation } from '../../common/BcCalculationDetails';
import type { BoqSummaryLineItem } from '../../../utils/groundworksBoqSummaryComputer';
import { COLORS, formatUnit } from '../../../utils/constants';
import {
  buildStormSummaryData,
  type StormSummaryRow,
  type StormFormulaInfo,
} from '../../../utils/stormSummaryUtils';
import { getPondsRows, DEFAULT_POND_SECTIONS } from '../../../utils/stormBuildUpPonds';
import { getSwalesRows, DEFAULT_SWALE_SECTIONS } from '../../../utils/stormBuildUpSwales';
import { HEADWALL_ROWS } from '../../../utils/stormBuildUpHeadwalls';
import { withDashedTitleSuffix } from '../../../utils/stormBuildUpTitles';
import { getCellularAttenuationRows, DEFAULT_CELLULAR_SECTIONS } from '../../../utils/stormBuildUpCellularAttenuation';
import { getWeholiteAttenuationRows, DEFAULT_WEHOLITE_SECTIONS } from '../../../utils/stormBuildUpWeholiteAttenuation';
import { getConcAttenuationRows, DEFAULT_CONC_SECTIONS } from '../../../utils/stormBuildUpConcAttenuation';
import { getBoxCulvertsRows, DEFAULT_BOX_CULVERT_SECTIONS } from '../../../utils/stormBuildUpBoxCulverts';
import { findPondsSwalesEvaluateCode, findHeadwallEvaluateCode } from '../../../utils/stormPondsSwalesEvaluateCodesMapping';
import { findCellularAttenuationEvaluateCode } from '../../../utils/stormCellularAttenuationEvaluateCodesMapping';
import { findWeholiteAttenuationEvaluateCode } from '../../../utils/stormWeholiteAttenuationEvaluateCodesMapping';
import { resolveConcAttenuationEvaluateCode } from '../../../utils/stormConcAttenuationEvaluateCodesMapping';
import { findBoxCulvertsEvaluateCode } from '../../../utils/stormBoxCulvertsEvaluateCodesMapping';
import type { StormBuildUpRowDef } from '../../../utils/stormBuildUpTypes';
import StormBuildUpBillSummary from './StormBuildUpBillSummary';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows, type BillSummaryExtraRow } from '../../../hooks/useBillSummaryExtraItems';
import { computeDynamicBcCalculation, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';
import type { DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';

const { Text, Title } = Typography;

const fmt = (n: number) =>
  n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtCurrency = (n: number) =>
  `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Ad-hoc extra rows (useBillSummaryExtraItems.tsx), same reasoning/shape as
// FoulSewersBillSummary.tsx's own ExtFoulRow. Extra items can attach at the
// root ("__root__" bucket) or graft directly onto any real section/pipe-type
// row (that row's own `key` as bucket). Extended locally rather than editing
// stormSummaryUtils.ts.
type ExtStormRow = StormSummaryRow & {
  isExtra?: boolean;
  nodeType?: 'section' | 'item';
  amountType?: 'number' | 'text';
  amountText?: string;
  // Pre-computed rate/amount from buildRootExtraRows, marked up the same way
  // this table's own Rate column now is.
  presetRate?: number;
  presetAmount?: number;
  // Which extraItems bucket this row lives in ("__root__", or a real row's
  // own key) — the Actions column's Edit/Delete/Add Item calls need this to
  // address the right bucket instead of assuming root.
  attachKey?: string;
};

const extraRowToStormRow = (row: BillSummaryExtraRow): ExtStormRow => ({
  key: row.key,
  rowType: row.nodeType === 'section' ? 'section' : 'item',
  description: row.description,
  quantity: row.quantity,
  unit: row.unit,
  isExtra: true,
  nodeType: row.nodeType,
  amountType: row.amountType,
  amountText: row.amountText,
  presetRate: row.amountType === 'text' ? undefined : parseFloat(row.rate || '0') || undefined,
  presetAmount: row.amountType === 'text' ? undefined : parseFloat(row.amount || '0') || undefined,
  attachKey: row.attachKey,
  children: row.children?.map(extraRowToStormRow),
});

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === 'section') {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const StormSewersBillSummary: React.FC = () => {
  const showDevMode = useDevMode();
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const { state, updateField } = useTender();

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [activeFormula, setActiveFormula] = useState<StormFormulaInfo | null>(null);
  // SummaryKey modal — "BCCalculation" independent backend-dbRate-only recomputation,
  // same as groundworks Bill Summaries (BcCalculationDetails.tsx / computeBcCalculation).
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<DynamicBillSummaryLineItem | null>(null);
  const [activeSection, setActiveSection] = useState<
    'summary' | 'ponds-swales' | 'cellular' | 'weholite' | 'conc' | 'box-culverts'
  >('summary');

  const { data: itemLibraryData } = useItemLibrary(state.region || '');
  // Same OH&P/Preliminaries markup every R&S Bill Summary applies to its own
  // Rate column now — baked into rateMap below so every downstream lookup
  // (Rate/Amount columns, sumRows/rowTotals, extra items, and the
  // StormBuildUpBillSummary child this rateMap is passed into for Ponds &
  // Swales/Cellular/Weholite/Conc Attenuation/Box Culverts) picks it up for
  // free.
  const markupFactor = useRoadsAndSewersMarkupFactor();

  // Root-level ad-hoc extra rows (see ExtStormRow above).
  const stormSewersExtraItems = state.stormSewersExtraItems;
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);
  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: stormSewersExtraItems,
    onChange: (updated) => updateField('stormSewersExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setNewlyAddedRowId(nodeId);
    },
  });

  // Rate is rounded to 2dp here — the SAME value shown in the Rate column —
  // so Amount always equals the displayed Rate × displayed Qty exactly (the
  // raw item-library rate can carry more than 2dp, which would otherwise
  // silently drift Amount away from what the user can verify by hand). Same
  // convention every groundworks Bill Summary's Amount column uses.
  const rateMap = useMemo(() => {
    const map: Record<string, number> = {};
    (itemLibraryData ?? []).forEach((item) => {
      if (item.resourceCode) map[item.resourceCode] = parseFloat(((item.nettRate || 0) * markupFactor).toFixed(2));
    });
    return map;
  }, [itemLibraryData, markupFactor]);

  // Plain nett rate, no markup — only for the dev-mode "BC" modals (this
  // table's own and each StormBuildUpBillSummary tab's), which show the
  // backend's dbRate-only recomputation for reconciliation.
  const nettRateMap = useMemo(() => {
    const map: Record<string, number> = {};
    (itemLibraryData ?? []).forEach((item) => {
      if (item.resourceCode) map[item.resourceCode] = parseFloat((item.nettRate || 0).toFixed(2));
    });
    return map;
  }, [itemLibraryData]);

  const storm = state.stormSewers;

  // Ponds & Swales (shares one table with Headwalls), Cellular/Weholite/Conc
  // Attenuation, and Box Culverts — same config-driven section counts
  // StormSewersTakeoff.tsx reads to build its own live editing tables.
  const pondSectionCount = state.pondsSwales?.config?.pondSectionCount ?? DEFAULT_POND_SECTIONS;
  const exTopsoilMm = Math.round((state.roads?.cutFillSections?.config?.stripValueDefault ?? 0.3) * 1000);
  const pondTopsoilThicknessMm = state.pondsSwales?.config?.pondTopsoilThicknessMm ?? {};
  const swaleSectionCount = state.pondsSwales?.config?.swaleSectionCount ?? DEFAULT_SWALE_SECTIONS;
  const swaleTopsoilThicknessMm = state.pondsSwales?.config?.swaleTopsoilThicknessMm ?? {};
  const pondsSwalesSectionTitles = state.pondsSwales?.config?.sectionTitles ?? {};
  const pondsSwalesRows: StormBuildUpRowDef[] = useMemo(() => withDashedTitleSuffix([
    ...getPondsRows(pondSectionCount, exTopsoilMm, pondTopsoilThicknessMm, pondsSwalesSectionTitles),
    ...getSwalesRows(swaleSectionCount, exTopsoilMm, swaleTopsoilThicknessMm, pondsSwalesSectionTitles),
    ...HEADWALL_ROWS,
  ]), [pondSectionCount, exTopsoilMm, pondTopsoilThicknessMm, swaleSectionCount, swaleTopsoilThicknessMm, pondsSwalesSectionTitles]);
  const resolvePondsSwalesEvaluateCode = (row: StormBuildUpRowDef): string =>
    row.id.startsWith('hw_')
      ? findHeadwallEvaluateCode(row.description) ?? ''
      : findPondsSwalesEvaluateCode(row.description) ?? '';
  // Expand/collapse state lifted up here (rather than owned inside
  // StormBuildUpBillSummary) so its button can sit on the same header line as
  // the section Segmented control, next to the Bill Summary tab's own button.
  const [pondsSwalesExpandedKeys, setPondsSwalesExpandedKeys] = useState<React.Key[]>([]);
  const [pondsSwalesNewlyAddedRowId, setPondsSwalesNewlyAddedRowId] = useState<string | null>(null);
  const pondsSwalesExtraItemsApi = useBillSummaryExtraItems({
    extraItems: state.pondsSwalesExtraItems,
    onChange: (updated) => updateField('pondsSwalesExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setPondsSwalesExpandedKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setPondsSwalesNewlyAddedRowId(nodeId);
      setTimeout(() => setPondsSwalesNewlyAddedRowId((prev) => (prev === nodeId ? null : prev)), 2000);
    },
  });

  const cellularSectionCount = state.cellularAttenuation?.config?.sectionCount ?? DEFAULT_CELLULAR_SECTIONS;
  const cellularCrateDims = state.cellularAttenuation?.config?.crateDims ?? {};
  const cellularSectionTitles = state.cellularAttenuation?.config?.sectionTitles ?? {};
  const cellularRows: StormBuildUpRowDef[] = useMemo(
    () => withDashedTitleSuffix(getCellularAttenuationRows(cellularSectionCount, undefined, cellularCrateDims, cellularSectionTitles)),
    [cellularSectionCount, cellularCrateDims, cellularSectionTitles],
  );
  const resolveCellularEvaluateCode = (row: StormBuildUpRowDef): string =>
    findCellularAttenuationEvaluateCode(row.description) ?? '';
  const [cellularExpandedKeys, setCellularExpandedKeys] = useState<React.Key[]>([]);
  const [cellularNewlyAddedRowId, setCellularNewlyAddedRowId] = useState<string | null>(null);
  const cellularExtraItemsApi = useBillSummaryExtraItems({
    extraItems: state.cellularAttenuationExtraItems,
    onChange: (updated) => updateField('cellularAttenuationExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setCellularExpandedKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setCellularNewlyAddedRowId(nodeId);
      setTimeout(() => setCellularNewlyAddedRowId((prev) => (prev === nodeId ? null : prev)), 2000);
    },
  });

  const weholiteSectionCount = state.weholiteAttenuation?.config?.sectionCount ?? DEFAULT_WEHOLITE_SECTIONS;
  const weholiteSupplierName = state.weholiteAttenuation?.config?.supplierName ?? 'Weholite';
  const weholiteConfigPct = state.weholiteAttenuation?.config?.weholiteConfigPct ?? {};
  const weholiteSectionTitles = state.weholiteAttenuation?.config?.sectionTitles ?? {};
  const weholiteRows: StormBuildUpRowDef[] = useMemo(
    () => withDashedTitleSuffix(getWeholiteAttenuationRows(weholiteSectionCount, weholiteConfigPct, weholiteSupplierName, weholiteSectionTitles)),
    [weholiteSectionCount, weholiteConfigPct, weholiteSupplierName, weholiteSectionTitles],
  );
  const resolveWeholiteEvaluateCode = (row: StormBuildUpRowDef): string =>
    findWeholiteAttenuationEvaluateCode(row.id) ?? '';
  const [weholiteExpandedKeys, setWeholiteExpandedKeys] = useState<React.Key[]>([]);
  const [weholiteNewlyAddedRowId, setWeholiteNewlyAddedRowId] = useState<string | null>(null);
  const weholiteExtraItemsApi = useBillSummaryExtraItems({
    extraItems: state.weholiteAttenuationExtraItems,
    onChange: (updated) => updateField('weholiteAttenuationExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setWeholiteExpandedKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setWeholiteNewlyAddedRowId(nodeId);
      setTimeout(() => setWeholiteNewlyAddedRowId((prev) => (prev === nodeId ? null : prev)), 2000);
    },
  });

  const concSectionCount = state.concAttenuation?.config?.sectionCount ?? DEFAULT_CONC_SECTIONS;
  const concConfigPct = state.concAttenuation?.config?.concConfigPct ?? {};
  const concSupplierName = state.concAttenuation?.config?.supplierName ?? 'Carlow';
  const concSectionTitles = state.concAttenuation?.config?.sectionTitles ?? {};
  const concDrawingRefs = state.concAttenuation?.config?.drawingRefs ?? {};
  const concRows: StormBuildUpRowDef[] = useMemo(
    () => withDashedTitleSuffix(getConcAttenuationRows(concSectionCount, concConfigPct, concSupplierName, concSectionTitles, concDrawingRefs)),
    [concSectionCount, concConfigPct, concSupplierName, concSectionTitles, concDrawingRefs],
  );
  const resolveConcEvaluateCode = (row: StormBuildUpRowDef): string =>
    resolveConcAttenuationEvaluateCode(row) ?? '';
  const [concExpandedKeys, setConcExpandedKeys] = useState<React.Key[]>([]);
  const [concNewlyAddedRowId, setConcNewlyAddedRowId] = useState<string | null>(null);
  const concExtraItemsApi = useBillSummaryExtraItems({
    extraItems: state.concAttenuationExtraItems,
    onChange: (updated) => updateField('concAttenuationExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setConcExpandedKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setConcNewlyAddedRowId(nodeId);
      setTimeout(() => setConcNewlyAddedRowId((prev) => (prev === nodeId ? null : prev)), 2000);
    },
  });

  const boxCulvertSectionCount = state.boxCulverts?.config?.sectionCount ?? DEFAULT_BOX_CULVERT_SECTIONS;
  const culvertConfigMm = state.boxCulverts?.config?.culvertConfigMm ?? {};
  const culvertBackfillType = state.boxCulverts?.config?.culvertBackfillType ?? {};
  const boxCulvertRows: StormBuildUpRowDef[] = useMemo(
    // The "Height" row (noQuant, informational only — shows the culvert's
    // configured height, set via the take-off page's own Configure modal) is
    // meaningful there but not something this Bill Summary needs to show —
    // per direct feedback, dropped here only. Take-off's own BoxCulvertsTable.tsx,
    // Sum Analysis totals (useRoadsAndSewersTotalSync.ts), and the persisted
    // BOQ (roadsAndSewersBoqSummaryComputer.ts) each call getBoxCulvertsRows
    // independently and are unaffected — it never carried a quantity/amount
    // anyway (noQuant rows are always blank), so nothing downstream changes.
    () => getBoxCulvertsRows(boxCulvertSectionCount, culvertConfigMm, culvertBackfillType)
      .filter((r) => r.itemLabel !== 'Height'),
    [boxCulvertSectionCount, culvertConfigMm, culvertBackfillType],
  );
  const resolveBoxCulvertsEvaluateCode = (row: StormBuildUpRowDef): string =>
    findBoxCulvertsEvaluateCode(row.description) ?? '';
  const [boxCulvertsExpandedKeys, setBoxCulvertsExpandedKeys] = useState<React.Key[]>([]);
  const [boxCulvertsNewlyAddedRowId, setBoxCulvertsNewlyAddedRowId] = useState<string | null>(null);
  const boxCulvertsExtraItemsApi = useBillSummaryExtraItems({
    extraItems: state.boxCulvertsExtraItems,
    onChange: (updated) => updateField('boxCulvertsExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) setBoxCulvertsExpandedKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      setBoxCulvertsNewlyAddedRowId(nodeId);
      setTimeout(() => setBoxCulvertsNewlyAddedRowId((prev) => (prev === nodeId ? null : prev)), 2000);
    },
  });

  const tableData: StormSummaryRow[] = useMemo(() => {
    const realRows = storm
      ? buildStormSummaryData(
          storm.pipeRuns.table || [],
          storm.manholes.table || [],
          storm.lateralConnections.table || [],
          storm.gullyConnections.table || [],
          storm.pipeRuns.config.eoQtys,
          storm.manholes.config.manholeScheduleConfig,
        )
      : [];
    // Graft ad-hoc extra rows onto this otherwise-computed tree — nested
    // additions merge onto the matching real section/pipe-type row (by
    // `key`), root additions become new top-level rows.
    const extraBuckets = stormSewersExtraItems ?? {};
    const injectExtraRows = (rowsIn: StormSummaryRow[]): StormSummaryRow[] =>
      rowsIn.map((row) => {
        const bucket = extraBuckets[row.key];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        const existingChildren = row.children ?? [];
        const extraRows = buildNestedExtraRows(bucket, row.key, row.key, existingChildren.length, markupFactor)
          .map(extraRowToStormRow);
        return { ...row, children: [...(ownChildren ?? []), ...extraRows] };
      });
    const injectedRealRows = injectExtraRows(realRows);
    const extraRootRows = buildRootExtraRows(extraBuckets['__root__'] ?? [], '__root__', 1, markupFactor)
      .map(extraRowToStormRow);
    return [...injectedRealRows, ...extraRootRows];
  }, [storm, stormSewersExtraItems, markupFactor]);

  const hasData =
    (storm?.pipeRuns?.table?.length ?? 0) > 0 ||
    (storm?.manholes?.table?.length ?? 0) > 0 ||
    (storm?.lateralConnections?.table?.length ?? 0) > 0 ||
    (storm?.gullyConnections?.table?.length ?? 0) > 0 ||
    Object.values(stormSewersExtraItems ?? {}).some((bucket) => bucket.length > 0);

  const sumRows = (rows: (StormSummaryRow & Partial<ExtStormRow>)[]): number =>
    rows.reduce((acc, row) => {
      if (row.children) return acc + sumRows(row.children);
      if (row.rowType !== 'item') return acc;
      if (row.isExtra) return acc + (row.amountType === 'text' ? 0 : (row.presetAmount ?? 0));
      const qty = parseFloat(row.quantity ?? '');
      const rate = row.evaluateCode ? (rateMap[row.evaluateCode] ?? 0) : 0;
      return isNaN(qty) ? acc : acc + qty * rate;
    }, 0);

  const totalAmount = useMemo(() => sumRows(tableData), [tableData, rateMap]);

  // Dev-mode only: pulls the persisted `roadAndSewers.summary` tree from the
  // same boq-versions API the rest of this review page uses, so the
  // SummaryKey columns (here, and in each of the five StormBuildUpBillSummary
  // tabs below) reflect what was actually saved rather than the live
  // recomputation above. One fetch is shared across all six domains.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );
  const persistedSummary = useMemo(
    () => (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ roadAndSewers?: { summary?: Partial<RoadsAndSewersBoqSummary> } }> } })
      ?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary,
    [latestBoqVersionData],
  );
  const persistedStormSewers = useMemo(
    () => flattenBoqSummaryLineItems(persistedSummary?.stormSewers),
    [persistedSummary],
  );
  const persistedPondsSwales = useMemo(
    () => flattenBoqSummaryLineItems(persistedSummary?.pondsSwales),
    [persistedSummary],
  );
  const persistedCellularAttenuation = useMemo(
    () => flattenBoqSummaryLineItems(persistedSummary?.cellularAttenuation),
    [persistedSummary],
  );
  const persistedWeholiteAttenuation = useMemo(
    () => flattenBoqSummaryLineItems(persistedSummary?.weholiteAttenuation),
    [persistedSummary],
  );
  const persistedConcAttenuation = useMemo(
    () => flattenBoqSummaryLineItems(persistedSummary?.concAttenuation),
    [persistedSummary],
  );
  const persistedBoxCulverts = useMemo(
    () => flattenBoqSummaryLineItems(persistedSummary?.boxCulverts),
    [persistedSummary],
  );

  // Pre-compute subtotals for every group row (section / pipe-type)
  const rowTotals = useMemo(() => {
    const map: Record<string, number> = {};
    const visit = (rows: StormSummaryRow[]) => {
      rows.forEach((row) => {
        if (row.children) {
          map[row.key] = sumRows(row.children);
          visit(row.children);
        }
      });
    };
    visit(tableData);
    return map;
  }, [tableData, rateMap]);

  // Recurses through every level (section → pipe-type → nested groups →
  // sub-groups) so "Expand All" actually opens every collapsible row, not
  // just sections and their direct children — same fix as
  // FoulSewersBillSummary.tsx's own allKeys. The previous 2-level walk
  // (ALL_SECTION_KEYS + one level of section.children) missed any row whose
  // own children were themselves grouped further.
  const allKeys = useMemo(() => {
    const keys: React.Key[] = [];
    const visit = (rows: StormSummaryRow[]) => {
      rows.forEach((row) => {
        if (row.children && row.children.length > 0) {
          keys.push(row.key);
          visit(row.children);
        }
      });
    };
    visit(tableData);
    return keys;
  }, [tableData]);

  const columns = [
    // Every column renders independently for every row (no colSpan merge
    // trick) — same convention groundworks/GroundworksCutFillBillSummary.tsx
    // uses: a group row shows its title only in Reference/Description, blank
    // in every other column, Amount shows the collapsed subtotal. colSpan
    // requires its value to exactly match the live column count on every
    // render (SummaryKey/Actions toggle independently here), and any drift
    // there visibly misaligns every column after it.
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 180,
      render: (_v: string, record: StormSummaryRow) => {
        if (record.rowType === 'section') {
          return <Text strong className="text-indigo-600" style={{ textTransform: 'uppercase' }}>{record.description}</Text>;
        }
        if (record.rowType === 'pipe-type') {
          return <Text strong className="text-gray-700" style={{ textTransform: 'uppercase' }}>{record.reference}</Text>;
        }
        return record.reference ? <Text>{record.reference}</Text> : null;
      },
    },
    // ── Col 2: Description (evaluate code as sub-line) ────────────────────────
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (v: string, record: StormSummaryRow) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type') return '';
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span>
              <Text>{v}</Text>
              {showDevMode && record.formulaInfo && (
                <InfoCircleOutlined
                  style={{ color: '#1677ff', marginLeft: 6, cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); setActiveFormula(record.formulaInfo ?? null); }}
                />
              )}
            </span>
            {showDevMode && record.evaluateCode && (
              <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>
                {record.evaluateCode}
              </div>
            )}
          </div>
        );
      },
    },
    // ── Col 3: Qty ────────────────────────────────────────────────────────────
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right' as const,
      render: (v: string, record: StormSummaryRow & Partial<ExtStormRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type')
          return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <NumberInput
              value={parseFloat(v) || 0}
              onChange={(qty) => extraItemsApi.updateNode(record.attachKey ?? '__root__', record.key as string, { qty })}
              size="small"
              decimalPlaces={2}
              autoFocus={record.key === newlyAddedRowId}
            />
          );
        }
        const n = parseFloat(v ?? '');
        if (isNaN(n)) return record.key === 'slat-1200-chamber' ? null : <Text>0</Text>;
        return <Text style={{ color: n < 0 ? '#cf1322' : undefined }}>{Math.round(n).toLocaleString('en-GB')}</Text>;
      },
    },
    // ── Col 4: Unit ───────────────────────────────────────────────────────────
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 70,
      align: 'center' as const,
      render: (v: string, record: StormSummaryRow & Partial<ExtStormRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type')
          return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <Select
              value={v || undefined}
              onChange={(unit) => extraItemsApi.updateNode(record.attachKey ?? '__root__', record.key as string, { unit })}
              options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
              placeholder="Unit"
              size="small"
              showSearch
              allowClear
              style={{ width: '100%' }}
            />
          );
        }
        return formatUnit(v) || '';
      },
    },
    // ── Col 5: Rate £ ─────────────────────────────────────────────────────────
    {
      title: 'Rate (£)',
      key: 'rate',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, record: StormSummaryRow & Partial<ExtStormRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type')
          return '';
        if (record.isExtra) {
          return record.presetRate != null ? <Text>{fmt(record.presetRate)}</Text> : <Text type="secondary">—</Text>;
        }
        if (!record.evaluateCode) return <Text type="secondary">—</Text>;
        const rate = rateMap[record.evaluateCode];
        return rate != null ? <Text>{fmt(rate)}</Text> : <Text type="secondary">—</Text>;
      },
    },
    // ── Col 6: Amount £ ───────────────────────────────────────────────────────
    {
      title: 'Amount (£)',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: StormSummaryRow & Partial<ExtStormRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type') {
          const isCollapsed = !expandedRowKeys.includes(record.key);
          if (isCollapsed) {
            const subtotal = rowTotals[record.key] ?? 0;
            return subtotal > 0 ? <Text strong>{fmtCurrency(subtotal)}</Text> : null;
          }
          return '';
        }
        if (record.amountType === 'text') return <Text>{record.amountText}</Text>;
        if (record.isExtra) {
          return record.presetAmount != null ? <Text>{fmt(record.presetAmount)}</Text> : <Text type="secondary">—</Text>;
        }
        if (!record.evaluateCode) return <Text type="secondary">—</Text>;
        const qty = parseFloat(record.quantity ?? '');
        const rate = rateMap[record.evaluateCode];
        if (isNaN(qty) || rate == null) return <Text type="secondary">—</Text>;
        return <Text>{fmt(qty * rate)}</Text>;
      },
    },
    // ── Dev-mode only: persisted-vs-live comparison column ───────────────────
    ...(showDevMode ? [{
      title: 'SummaryKey',
      key: 'summaryKey',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: StormSummaryRow & Partial<ExtStormRow>) => {
        if (record.rowType === 'section' || record.rowType === 'pipe-type')
          return '';
        if (record.isExtra) {
          const node = findExtraNode(stormSewersExtraItems?.[record.attachKey ?? '__root__'] ?? [], record.key as string) as DynamicBillSummaryItemNode | null;
          if (!node) return '';
          const liveAmount = record.presetAmount ?? 0;
          const lineItem: DynamicBillSummaryLineItem = {
            key: node.id, description: node.description, quantity: node.qty, unit: node.unit, rate: node.rate,
            amount: liveAmount, amountType: node.amountType, amountText: node.amountText,
            resourceCode: node.resourceCode, resourceCodes: node.resourceCodes,
          };
          const bc = computeDynamicBcCalculation(lineItem, (code) => nettRateMap[code] ?? 0);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text style={{ fontSize: 12, color: '#f59e0b' }}>unsaved</Text>
                <InfoCircleOutlined style={{ cursor: 'pointer', color: '#3b82f6' }} onClick={() => setExtraSummaryKeyRecord(lineItem)} />
              </div>
              {bc && <Text style={{ fontSize: 10, color: '#3b82f6' }}>BC: {fmtCurrency(bc.amount)}</Text>}
            </div>
          );
        }
        const persisted = persistedStormSewers.get(record.key);
        if (!persisted) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
        const qty = parseFloat(record.quantity ?? '');
        const rate = record.evaluateCode ? rateMap[record.evaluateCode] : undefined;
        const liveAmount = !isNaN(qty) && rate != null ? qty * rate : 0;
        const mismatch = Math.abs(liveAmount - (persisted.amount || 0)) > 0.01;
        const bcAmount = computeBcCalculation(persisted, (code) => nettRateMap[code] ?? 0).amount;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                {fmtCurrency(persisted.amount || 0)}
              </Text>
              <InfoCircleOutlined
                style={{ cursor: 'pointer', color: '#3b82f6' }}
                onClick={() => setSummaryKeyModalRecord(persisted)}
              />
            </div>
            <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
              BC: {fmtCurrency(bcAmount)}
            </Text>
          </div>
        );
      },
    }] : []),
    // ── Add Section/Add Item on any real or extra section/pipe-type row,
    // Edit/Delete on extra rows — a real header row is immutable itself but
    // can still host grafted extra rows. ────────────────────────────────────
    ...(dynamicSummaryEnabled && !state.isViewMode ? [{
      title: 'Actions',
      key: 'extraActions',
      width: 150,
      align: 'center' as const,
      render: (_: unknown, record: StormSummaryRow & Partial<ExtStormRow>) => {
        if (record.isExtra) {
          const bucketKey = record.attachKey ?? '__root__';
          const node = findExtraNode(stormSewersExtraItems?.[bucketKey] ?? [], record.key as string);
          if (!node) return '';
          if (record.nodeType === 'section') {
            return (
              <Space size={4}>
                <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(bucketKey, record.key as string)} />
                <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(bucketKey, node as DynamicBillSummarySectionNode)} />
                <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(bucketKey, record.key as string)} />
              </Space>
            );
          }
          return (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(bucketKey, node as DynamicBillSummaryItemNode)} />
              <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(bucketKey, record.key as string)} />
            </Space>
          );
        }
        if (record.rowType === 'section' || record.rowType === 'pipe-type') {
          return (
            <Space size={4}>
              <Button size="small" icon={<FolderAddOutlined />} title="Add Section" onClick={() => extraItemsApi.openAddSection(record.key as string, null)} />
              <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key as string, null)} />
            </Space>
          );
        }
        return '';
      },
    }] : []),
  ];

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: '16px' }}>
        <Title level={5} style={{ margin: '0 0 12px', fontWeight: 600, fontSize: '16px', color: '#111827' }}>
          Storm Sewers Bill Summary
        </Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <Segmented
            value={activeSection}
            onChange={(value) => setActiveSection(value as typeof activeSection)}
            options={[
              { label: 'Bill Summary', value: 'summary' },
              { label: 'Ponds & Swales', value: 'ponds-swales' },
              { label: 'Cellular Attenuation', value: 'cellular' },
              { label: `${weholiteSupplierName} Attenuation`, value: 'weholite' },
              { label: 'Conc Attenuation', value: 'conc' },
              { label: 'Box Culverts', value: 'box-culverts' },
            ]}
          />
          {activeSection === 'summary' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={expandedRowKeys.length > 0}
                onToggle={() => setExpandedRowKeys(expandedRowKeys.length > 0 ? [] : allKeys)}
              />
            </Space>
          )}
          {activeSection === 'ponds-swales' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => pondsSwalesExtraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={pondsSwalesExpandedKeys.length > 0}
                onToggle={() => setPondsSwalesExpandedKeys(
                  pondsSwalesExpandedKeys.length > 0 ? [] : pondsSwalesRows.filter((r) => r.isHeader).map((r) => r.id),
                )}
              />
            </Space>
          )}
          {activeSection === 'cellular' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => cellularExtraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={cellularExpandedKeys.length > 0}
                onToggle={() => setCellularExpandedKeys(
                  cellularExpandedKeys.length > 0 ? [] : cellularRows.filter((r) => r.isHeader).map((r) => r.id),
                )}
              />
            </Space>
          )}
          {activeSection === 'weholite' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => weholiteExtraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={weholiteExpandedKeys.length > 0}
                onToggle={() => setWeholiteExpandedKeys(
                  weholiteExpandedKeys.length > 0 ? [] : weholiteRows.filter((r) => r.isHeader).map((r) => r.id),
                )}
              />
            </Space>
          )}
          {activeSection === 'conc' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => concExtraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={concExpandedKeys.length > 0}
                onToggle={() => setConcExpandedKeys(
                  concExpandedKeys.length > 0 ? [] : concRows.filter((r) => r.isHeader).map((r) => r.id),
                )}
              />
            </Space>
          )}
          {activeSection === 'box-culverts' && (
            <Space>
              {dynamicSummaryEnabled && !state.isViewMode && (
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => boxCulvertsExtraItemsApi.openAddSection('__root__', null)}>
                  Add Section
                </Button>
              )}
              <ExpandCollapseAllButton
                expanded={boxCulvertsExpandedKeys.length > 0}
                onToggle={() => setBoxCulvertsExpandedKeys(
                  boxCulvertsExpandedKeys.length > 0 ? [] : boxCulvertRows.filter((r) => r.isHeader).map((r) => r.id),
                )}
              />
            </Space>
          )}
        </div>
      </div>

      <style>{`
        .storm-bill-table .ant-table { border-radius: 0 !important; }
        .storm-bill-table .ant-table-container { border-radius: 0 !important; }
        .storm-bill-table .ant-table-thead > tr > th { background-color: ${COLORS.tableHeaderBg} !important; }
        .storm-bill-table .ant-table-tbody > tr:hover > td { background-color: #fafafa !important; }
        .storm-bill-table .ant-table-tbody > tr.bg-indigo-50:hover > td { background-color: #e0e7ff !important; }
        .storm-bill-table .ant-table-tbody > tr.bg-gray-50:hover > td { background-color: #f3f4f6 !important; }
      `}</style>

      {activeSection === 'summary' && (hasData ? (
        <Table<StormSummaryRow>
          className="storm-bill-table"
          dataSource={tableData}
          columns={columns}
          rowKey="key"
          bordered
          size="middle"
          pagination={false}
          expandable={{
            childrenColumnName: 'children',
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys(
                expanded
                  ? [...expandedRowKeys, record.key]
                  : expandedRowKeys.filter((k) => k !== record.key),
              );
            },
            expandRowByClick: true,
          }}
          rowClassName={(record: StormSummaryRow & Partial<ExtStormRow>) => {
            const classes: string[] = [];
            if (record.rowType === 'section') classes.push('bg-indigo-50', 'font-semibold');
            else if (record.rowType === 'pipe-type') classes.push('bg-gray-50', 'font-semibold');
            if (record.key === newlyAddedRowId) classes.push('storm-summary-row-added');
            return classes.join(' ');
          }}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={5}>
                <Text strong style={{ fontWeight: 700 }}>TOTAL STORM SEWERS</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">
                <Text strong>{fmtCurrency(totalAmount)}</Text>
              </Table.Summary.Cell>
              {showDevMode && <Table.Summary.Cell index={2} />}
              {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={showDevMode ? 3 : 2} />}
            </Table.Summary.Row>
          )}
        />
      ) : (
        <div style={{ padding: '32px', textAlign: 'center' }}>
          <Text type="secondary">No storm sewer data entered. Enter data on the Storm Sewers Take-off page.</Text>
        </div>
      ))}
      <style>{`
        @keyframes storm-summary-row-added-fade { 0% { background-color: #fef3c7; } 100% { background-color: transparent; } }
        .storm-summary-row-added > td { animation: storm-summary-row-added-fade 1.8s ease-out; }
      `}</style>

      {activeSection === 'ponds-swales' && (
        <StormBuildUpBillSummary
          title="Ponds & Swales"
          rows={pondsSwalesRows}
          buildUp={state.pondsSwales?.table}
          rateMap={rateMap}
          nettRateMap={nettRateMap}
          markupFactor={markupFactor}
          resolveEvaluateCode={resolvePondsSwalesEvaluateCode}
          expandedRowKeys={pondsSwalesExpandedKeys}
          onExpandedRowKeysChange={setPondsSwalesExpandedKeys}
          persistedItems={persistedPondsSwales}
          extraItems={state.pondsSwalesExtraItems}
          extraItemsApi={pondsSwalesExtraItemsApi}
          dynamicSummaryEnabled={dynamicSummaryEnabled}
          newlyAddedRowId={pondsSwalesNewlyAddedRowId}
        />
      )}

      {activeSection === 'cellular' && (
        <StormBuildUpBillSummary
          title="Cellular Attenuation"
          rows={cellularRows}
          buildUp={state.cellularAttenuation?.table}
          rateMap={rateMap}
          nettRateMap={nettRateMap}
          markupFactor={markupFactor}
          resolveEvaluateCode={resolveCellularEvaluateCode}
          expandedRowKeys={cellularExpandedKeys}
          onExpandedRowKeysChange={setCellularExpandedKeys}
          persistedItems={persistedCellularAttenuation}
          extraItems={state.cellularAttenuationExtraItems}
          extraItemsApi={cellularExtraItemsApi}
          dynamicSummaryEnabled={dynamicSummaryEnabled}
          newlyAddedRowId={cellularNewlyAddedRowId}
        />
      )}

      {activeSection === 'weholite' && (
        <StormBuildUpBillSummary
          title={`${weholiteSupplierName} Attenuation`}
          rows={weholiteRows}
          buildUp={state.weholiteAttenuation?.table}
          rateMap={rateMap}
          nettRateMap={nettRateMap}
          markupFactor={markupFactor}
          resolveEvaluateCode={resolveWeholiteEvaluateCode}
          expandedRowKeys={weholiteExpandedKeys}
          onExpandedRowKeysChange={setWeholiteExpandedKeys}
          persistedItems={persistedWeholiteAttenuation}
          extraItems={state.weholiteAttenuationExtraItems}
          extraItemsApi={weholiteExtraItemsApi}
          dynamicSummaryEnabled={dynamicSummaryEnabled}
          newlyAddedRowId={weholiteNewlyAddedRowId}
        />
      )}

      {activeSection === 'conc' && (
        <StormBuildUpBillSummary
          title="Conc Attenuation"
          rows={concRows}
          buildUp={state.concAttenuation?.table}
          rateMap={rateMap}
          nettRateMap={nettRateMap}
          markupFactor={markupFactor}
          resolveEvaluateCode={resolveConcEvaluateCode}
          expandedRowKeys={concExpandedKeys}
          onExpandedRowKeysChange={setConcExpandedKeys}
          persistedItems={persistedConcAttenuation}
          extraItems={state.concAttenuationExtraItems}
          extraItemsApi={concExtraItemsApi}
          dynamicSummaryEnabled={dynamicSummaryEnabled}
          newlyAddedRowId={concNewlyAddedRowId}
        />
      )}

      {activeSection === 'box-culverts' && (
        <StormBuildUpBillSummary
          title="Box Culverts"
          rows={boxCulvertRows}
          buildUp={state.boxCulverts?.table}
          rateMap={rateMap}
          nettRateMap={nettRateMap}
          markupFactor={markupFactor}
          resolveEvaluateCode={resolveBoxCulvertsEvaluateCode}
          expandedRowKeys={boxCulvertsExpandedKeys}
          onExpandedRowKeysChange={setBoxCulvertsExpandedKeys}
          persistedItems={persistedBoxCulverts}
          extraItems={state.boxCulvertsExtraItems}
          extraItemsApi={boxCulvertsExtraItemsApi}
          dynamicSummaryEnabled={dynamicSummaryEnabled}
          newlyAddedRowId={boxCulvertsNewlyAddedRowId}
        />
      )}

      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => nettRateMap[code] ?? 0}
          />
        )}
      </Modal>

      <Modal
        title="SummaryKey — Live Value (not yet in BOQ export)"
        open={!!extraSummaryKeyRecord}
        onCancel={() => setExtraSummaryKeyRecord(null)}
        footer={null}
      >
        {extraSummaryKeyRecord && (() => {
          const bc = computeDynamicBcCalculation(extraSummaryKeyRecord, (code) => nettRateMap[code] ?? 0);
          return (
            <div>
              <Typography.Paragraph>
                <Text strong>Description: </Text>{extraSummaryKeyRecord.description}
              </Typography.Paragraph>
              {extraSummaryKeyRecord.quantity != null && (
                <Typography.Paragraph><Text strong>Quantity: </Text>{extraSummaryKeyRecord.quantity}</Typography.Paragraph>
              )}
              {extraSummaryKeyRecord.unit && (
                <Typography.Paragraph><Text strong>Unit: </Text>{formatUnit(extraSummaryKeyRecord.unit)}</Typography.Paragraph>
              )}
              {extraSummaryKeyRecord.rate != null && (
                <Typography.Paragraph><Text strong>Rate: </Text>{fmtCurrency(extraSummaryKeyRecord.rate)}</Typography.Paragraph>
              )}
              <Typography.Paragraph><Text strong>Amount: </Text>{fmtCurrency(extraSummaryKeyRecord.amount)}</Typography.Paragraph>
              {bc && (
                <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                  <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                  <div style={{ marginTop: 8 }}>
                    {bc.steps.map((step, idx) => <div key={idx}><Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text></div>)}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </Modal>

      {extraItemsApi.modals}

      <Drawer
        title={activeFormula?.label ?? 'Formula Detail'}
        open={!!activeFormula}
        onClose={() => setActiveFormula(null)}
        width={460}
        styles={{ body: { padding: '16px' } }}
      >
        {activeFormula && (
          <div>
            <Typography.Paragraph>
              <Text strong>Formula: </Text>
              <Text code>{activeFormula.formula}</Text>
            </Typography.Paragraph>
            {activeFormula.rows.length > 0 && (
              <Table
                size="small"
                bordered
                pagination={false}
                dataSource={activeFormula.rows.map((r, i) => ({ ...r, key: i }))}
                columns={[
                  { title: 'Source', dataIndex: 'ref', key: 'ref' },
                  {
                    title: 'Value', dataIndex: 'value', key: 'value', align: 'right' as const,
                    render: (v: number) => fmt(v),
                  },
                  { title: 'Unit', dataIndex: 'unit', key: 'unit', align: 'right' as const, render: (v: string) => formatUnit(v) },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><Text strong>Total</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">
                      <Text strong>{fmt(activeFormula.total)}</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                  </Table.Summary.Row>
                )}
              />
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default StormSewersBillSummary;
